import {
  IMAGE_COMING_SOON,
  MAX_IMAGE_EXPORT_BYTES,
  MAX_IMAGE_EXPORT_DIMENSION,
} from '~/core/constants';
import {
  type CanvasImageUrlProps,
  changeAndRevertCanvasAppearance,
  dimensionScaler,
  getCanvasBoundingBox,
  getCanvasImageDimensions,
  makeCanvasImageUrl,
  scaleMaxDimension,
} from '~/core/helpers';

/**
 * Generates a JPEG/PNG representation of the canvas
 * The function performs various safety checks, temporarily modifies object styles
 * and background color for a consistent export, and then restores the original state.
 */
export const generateCanvasImage = (
  format: CanvasImageUrlProps['format'],
  canvas?: fabric.Canvas
): string => {
  if (!canvas) {
    return IMAGE_COMING_SOON;
  }

  const box = getCanvasBoundingBox(canvas);

  if (!box) {
    return IMAGE_COMING_SOON;
  }

  const dimensions = getCanvasImageDimensions(canvas, box);

  if (!dimensions) {
    return IMAGE_COMING_SOON;
  }

  const { width, height, leftOffset, topOffset } = dimensions;

  if (!isFinite(width) || !isFinite(height)) {
    return IMAGE_COMING_SOON;
  }

  let url = '';

  changeAndRevertCanvasAppearance(canvas, (modifiedCanvas) => {
    let retryAttempts = 3; // 1: normal, 2: extreme, 3: edge cases, 5: way out
    let maxDimension = MAX_IMAGE_EXPORT_DIMENSION;

    while (
      retryAttempts &&
      (!url.length || url.length > MAX_IMAGE_EXPORT_BYTES)
    ) {
      maxDimension = scaleMaxDimension(maxDimension, url.length);
      url = makeCanvasImageUrl({
        canvas: modifiedCanvas,
        format,
        scaleFactor: dimensionScaler(width, height, maxDimension),
        leftOffset,
        topOffset,
        width,
        height,
      });

      retryAttempts--;
    }
  });

  return url;
};
